import FileSaver from "file-saver";
import apiClient from "../../../apis/api-client";
import { Sort } from "../../../domain/pagination/sort";
import { toast } from "react-toastify";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../../domain/pagination/paging";
import { ApplicationEntity } from "../../../domain/application/application-entity";
import { rebuildQuery } from "../../../../utils/url-utils";
import {
  ProcedureEntity,
  ProcedureType,
} from "../../../domain/procedures/procedure-entity";
import {
  SmartApplyApiError,
  reportUnexpectedError,
  BaseApplicationService,
  nameCsvFileWithTerm,
  GetApplicationsByProcedureQueryVariables,
} from "@graffer-inc/smart-apply";
import { formatDate } from "../../../../utils/date-utils";
import { ApplicantSchemaKeys, ApplyStatus } from "@graffer-inc/cloud-gov-core";
import { StatusUpdateErrorCodeLiterals } from "./error-messages";
import { CSVEncoding } from "../../../domain/file-encoding";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { ApplicationFactory } from "../../../domain/application/application-factory";
import { SmartApplyApiErrorFromGqlError } from "@graffer-inc/smart-apply";

export type SelectedCondition = {
  applicationIds: Array<string>;
  csv?: {
    name: string;
    data: string;
  };
};

export type BatchUpdateStatusResult = {
  done?: {
    count: number;
    errors: {
      id: string;
      errorCode: StatusUpdateErrorCodeLiterals;
    }[];
  };
  failure?: {
    message: string;
    applicationIDs?: string[];
  };
};

export type ApplicationsContextProps = {
  isLoading: boolean;
  openIssueFileModal: boolean;
  sort: Sort;
  selectedProcedureId?: string;
  selectedProcedure?: ProcedureEntity;
  applications: Array<{
    application: ApplicationEntity;
    applicantSchemaKeys: ApplicantSchemaKeys;
  }>;
  selected: SelectedCondition;
  selectedMetaIds: Array<string>;
  paging: {
    current: number;
    total: number;
  };
  dateRange: { from?: Date; to?: Date };
  selectedStatus: ApplyStatus[];
  batchUpdateStatusResult?: BatchUpdateStatusResult;
  isDownloadingSamplePDF: boolean;
  selectApplicationToggle: typeof ApplicationsService.prototype.selectApplicationToggle;
  selectAll: typeof ApplicationsService.prototype.selectAll;
  selectByCSV: typeof ApplicationsService.prototype.selectByCSV;
  isAllSelected: typeof ApplicationsService.prototype.isAllSelected;
  searchApplications: typeof ApplicationsService.prototype.searchApplications;
  fetchApplications: typeof ApplicationsService.prototype.fetchApplications;
  batchUpdateStatus: typeof ApplicationsService.prototype.batchUpdateStatus;
  clearBatchUpdateStatusResult: typeof ApplicationsService.prototype.clearBatchUpdateStatusResult;
  toggleCsvMeta: typeof ApplicationsService.prototype.toggleCsvMeta;
  toggleAllCsvMeta: typeof ApplicationsService.prototype.toggleAllCsvMeta;
  downloadCSV: typeof ApplicationsService.prototype.downloadCSV;
  setPage: typeof ApplicationsService.prototype.setPage;
  movePage: typeof ApplicationsService.prototype.movePage;
  setDateRange: typeof ApplicationsService.prototype.setDateRange;
  setSelectedStatus: typeof ApplicationsService.prototype.setSelectedStatus;
  setSort: typeof ApplicationsService.prototype.setSort;
  setOpenIssueFile: typeof ApplicationsService.prototype.setOpenIssueFile;
  setSelectedProcedureId: typeof ApplicationsService.prototype.setSelectedProcedureId;
  batchIssueFiles: typeof ApplicationsService.prototype.batchIssueFiles;
  batchUpdateTenantProcessResult: typeof ApplicationsService.prototype.batchUpdateTenantProcessResult;
  downloadSamplePDF: typeof ApplicationsService.prototype.downloadSamplePDF;
};

export class ApplicationsService extends BaseApplicationService<ApplicationsContextProps> {
  private readonly applicationCountPerPage: number = DefaultNumItemsPerPages;

  state: ApplicationsContextProps = {
    isLoading: false,
    openIssueFileModal: false,
    selectedProcedureId: undefined as string | undefined,
    selectedProcedure: undefined as ProcedureEntity | undefined,
    applications: [],
    selected: {
      applicationIds: [],
    },
    selectedMetaIds: [],
    sort: "desc" as Sort,
    paging: {
      current: 1,
      total: 1,
    },
    dateRange: {
      from: undefined,
      to: undefined,
    },
    selectedStatus: [],
    isDownloadingSamplePDF: false,
    selectApplicationToggle: this.selectApplicationToggle.bind(this),
    selectAll: this.selectAll.bind(this),
    selectByCSV: this.selectByCSV.bind(this),
    isAllSelected: this.isAllSelected.bind(this),
    searchApplications: this.searchApplications.bind(this),
    fetchApplications: this.fetchApplications.bind(this),
    batchUpdateStatus: this.batchUpdateStatus.bind(this),
    clearBatchUpdateStatusResult: this.clearBatchUpdateStatusResult.bind(this),
    toggleCsvMeta: this.toggleCsvMeta.bind(this),
    toggleAllCsvMeta: this.toggleAllCsvMeta.bind(this),
    downloadCSV: this.downloadCSV.bind(this),
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setDateRange: this.setDateRange.bind(this),
    setSelectedStatus: this.setSelectedStatus.bind(this),
    setSort: this.setSort.bind(this),
    setSelectedProcedureId: this.setSelectedProcedureId.bind(this),
    setOpenIssueFile: this.setOpenIssueFile.bind(this),
    batchIssueFiles: this.batchIssueFiles.bind(this),
    batchUpdateTenantProcessResult:
      this.batchUpdateTenantProcessResult.bind(this),
    downloadSamplePDF: this.downloadSamplePDF.bind(this),
  };

  setOpenIssueFile(openIssueFile: boolean) {
    this.setState({
      openIssueFileModal: openIssueFile,
    });
  }

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setDateRange(dateRange: { from?: Date; to?: Date }) {
    this.setState({
      dateRange,
    });
  }

  setSelectedStatus(status: ApplyStatus[]) {
    this.setState({
      selectedStatus: status,
    });
    this.history.replace(
      rebuildQuery({
        status: status,
      }),
    );
  }

  setSelectedProcedureId(procedureId?: string) {
    this.setState({ selectedProcedureId: procedureId });
    this.history.replace(
      rebuildQuery({
        procedureId: procedureId ?? "",
      }),
    );
  }

  setSort(sort: string | undefined) {
    if ("asc" === sort || "desc" === sort) {
      this.setState({ sort });
    } else {
      this.setState({ sort: "desc" });
    }
  }

  selectByCSV(file?: { name: string; data: string }) {
    this.setState({
      selected: {
        applicationIds: [],
        csv: file,
      },
    });
  }

  isAllSelected() {
    const applicationIds = this.state.applications.map((a) => a.application.id);
    const selectedApplicationIds = this.state.selected.applicationIds;
    return applicationIds.every((v) => selectedApplicationIds.includes(v));
  }

  selectApplicationToggle(id: string) {
    const { applicationIds } = this.state.selected;
    const found = this.state.applications.find((a) => a.application.id === id);
    if (!found) {
      throw new Error(`Illegal select application`);
    }

    this.setState({
      selected: {
        applicationIds: applicationIds.find((aid) => aid === id)
          ? applicationIds.filter((aid) => aid !== id)
          : [...applicationIds, id],
      },
    });
  }

  selectAll() {
    if (this.isAllSelected()) {
      this.setState({
        selected: {
          applicationIds: [],
        },
      });
    } else {
      this.setState({
        selected: {
          applicationIds: [
            ...this.state.applications.map((app) => app.application.id),
          ],
        },
      });
    }
  }

  searchApplications() {
    const date = this.state.dateRange;
    const fromStr = date.from ? formatDate(date.from as Date) : "";
    const toStr = date.to ? formatDate(date.to as Date) : "";
    this.history.replace(
      rebuildQuery({
        page: "1", // clear paging when search conditions have changed
        from: fromStr,
        to: toStr,
        sort: this.state.sort,
      }),
    );
  }

  async fetchApplications() {
    if (this.state.selectedProcedureId) {
      await this.fetchApplicationsByProcedure();
    } else {
      await this.fetchApplicationsWithoutProcedureId();
    }
  }

  toggleCsvMeta(id: string) {
    const { selectedMetaIds } = this.state;
    this.setState({
      selectedMetaIds: selectedMetaIds.includes(id)
        ? selectedMetaIds.filter((m) => m !== id)
        : [...selectedMetaIds, id],
    });
  }

  toggleAllCsvMeta(checked: boolean) {
    this.setState({
      selectedMetaIds: checked
        ? this.state.selectedProcedure!.allCSVMetaField().map((m) => m.id)
        : Array<string>(),
    });
  }

  async downloadCSV(
    csvEncoding: CSVEncoding,
    includeTenantProcessResult: boolean,
    requireSelectableRawValue: boolean,
    offset?: number,
  ) {
    const dates: { from?: Date; to?: Date } = this.state.dateRange;
    const allApplicationMetaId = this.state
      .selectedProcedure!.allCSVMetaField()
      .map((m) => m.id);
    const applicationMetaIds = allApplicationMetaId.filter((id) =>
      this.state.selectedMetaIds.includes(id),
    );
    try {
      const { data } = await apiClient.downloadProcedureApplicationsCSV(
        this.state.selectedProcedureId!,
        {
          dateFrom: dates.from ? formatDate(dates.from) : undefined,
          dateTo: dates.to ? formatDate(dates.to) : undefined,
        },
        this.state.selectedStatus,
        applicationMetaIds,
        csvEncoding,
        offset,
        this.state.selected.applicationIds,
        {
          includeTenantProcessResult,
          requireSelectableRawValue,
        },
      );
      FileSaver.saveAs(
        data,
        nameCsvFileWithTerm(this.state.selectedProcedure!.name, {
          from: dates.from,
          to: dates.to,
        }),
      );
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      switch (apiError.errorCode()) {
        case "bad_request.too_large_record_count":
          toast.error(
            "対象の申請件数が出力上限を超えました。指定期間を狭めてください。",
          );
          return;
        case "applications.cert_verification_pending":
          toast.error(
            "電子署名が検証中の申請があります。しばらく時間を置いて再度ダウンロードをしてください。",
          );
          return;
      }
      reportUnexpectedError(e);
      toast.error("CSVダウンロードでエラーが発生しました");
    }
  }

  clearBatchUpdateStatusResult() {
    this.setState({
      batchUpdateStatusResult: undefined,
    });
  }

  async batchUpdateStatus(status: ApplyStatus, skipNotification: boolean) {
    const procId = this.state.selectedProcedureId;
    if (!procId) {
      return;
    }
    if (
      this.state.selected.applicationIds.length === 0 &&
      !this.state.selected.csv
    ) {
      toast.error("ステータスを更新したい申請を選択してください。");
      return;
    }
    const request = this.state.selected.csv
      ? {
          csv: this.state.selected.csv.data,
        }
      : {
          ids: this.state.selected.applicationIds,
        };
    try {
      const { data: results } = await apiClient.batchUpdateApplicationStatus({
        ...request,
        procedureId: procId,
        status,
        skipNotification,
      });
      const errors = results.filter((result) => !!result.errorCode) as {
        id: string;
        errorCode: StatusUpdateErrorCodeLiterals;
      }[];
      if (errors.length > 0) {
        toast.warning("ステータス一括更新で一部の更新に失敗しました。");
      } else {
        toast.success("ステータスを一括更新しました。");
      }
      this.setState({
        batchUpdateStatusResult: {
          done: {
            count: results.length,
            errors: errors,
          },
        },
      });
      this.fetchApplications();
    } catch (e) {
      let message: string | undefined = undefined;
      if (SmartApplyApiError.isRetryable(e)) {
        message =
          "ネットワークエラーが発生しました。リトライをお試しください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.invalid_csv_format")
      ) {
        message =
          "CSVの形式が不正です。CSVアップロードモーダルにあるサンプルCSVを確認して修正してください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.too_large_record_count")
      ) {
        message = `1度に操作できる申請は500件までです。複数回に分けて操作をしてください。`;
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.procedure_not_match")
      ) {
        message = `「${
          this.state.selectedProcedure?.name ?? ""
        }」以外の手続きに対する申請が選択されています。以下の申請IDをCSVファイルから取り除いてください。`;
      }
      if (typeof message === "undefined") {
        message =
          "対応ステータス一括更新でエラーが発生しました。時間をおいて再度実行してください。";
        reportUnexpectedError(e);
      }
      toast.error(message);
      this.setState({
        batchUpdateStatusResult: {
          failure: {
            message: message,
            applicationIDs: e.response?.data?.applicationIDs,
          },
        },
      });
    }
  }

  private async fetchApplicationsWithoutProcedureId() {
    this.setIsLoading(true);

    try {
      const param = {
        page: this.state.paging.current,
        limit: this.applicationCountPerPage,
        sort: this.state.sort,
        from: this.state.dateRange.from
          ? formatDate(this.state.dateRange.from)
          : undefined,
        to: this.state.dateRange.to
          ? formatDate(this.state.dateRange.to)
          : undefined,
        status:
          this.state.selectedStatus.length > 0
            ? this.state.selectedStatus
            : undefined,
      };
      const { error, data } = await runGqlQueryV2(() =>
        gqlClient.getApplicationsWithApplicantSchemaKeys(param, gqlHeader("q")),
      );
      if (error) {
        throw SmartApplyApiErrorFromGqlError(error);
      }
      if (!data) {
        throw new Error(
          `query getApplicationsWithApplicantSchemaKeys ${JSON.stringify(
            param,
          )} does not return both error & data`,
        );
      }
      const { applications, totalCount } =
        data.applicationsWithApplicantSchemaKeys;

      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(totalCount, this.applicationCountPerPage),
        },
        applications: applications.map(
          ({ application, applicantSchemaKeys }) => ({
            application: ApplicationFactory.createFromGql(application),
            applicantSchemaKeys,
          }),
        ),
        selected: {
          applicationIds: [],
        },
        selectedProcedure: undefined,
      });
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "unauthorized")) {
        SmartApplyApiError.handle401Error();
        return;
      }
      toast.error(
        "申請一覧の取得に失敗しました。時間をおいて再度実行してください。",
      );
      reportUnexpectedError(e);
    } finally {
      this.setIsLoading(false);
    }
  }

  private async fetchApplicationsByProcedure() {
    this.setIsLoading(true);

    try {
      const date = this.state.dateRange;
      const param: GetApplicationsByProcedureQueryVariables = {
        procedureId: this.state.selectedProcedureId!,
        page: this.state.paging.current,
        limit: this.applicationCountPerPage,
        from: date.from ? formatDate(date.from) : undefined,
        to: date.to ? formatDate(date.to) : undefined,
        status: this.state.selectedStatus,
      };

      const { error, data } = await runGqlQueryV2(() =>
        gqlClient.getApplicationsByProcedure(param, gqlHeader("q")),
      );
      if (error) {
        throw SmartApplyApiErrorFromGqlError(error);
      }
      if (!data) {
        throw new Error(
          `query getApplicationsByProcedure ${JSON.stringify(param)} does not return both error & data`,
        );
      }
      const {
        applications: appls,
        procedure: proc,
        totalCount,
      } = data.applicationsByProcedure;
      const procedure = ProcedureEntity.fromJSON(proc as ProcedureType);
      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(totalCount, this.applicationCountPerPage),
        },
        selectedProcedure: procedure,
        selectedMetaIds: procedure
          .allCSVMetaField()
          .filter((v) => v.default)
          .map((v) => v.id),
        applications:
          appls.map((application) => ({
            application: ApplicationFactory.createFromGql(application),
            applicantSchemaKeys: procedure.getLiveDetail().applicantSchemaKeys,
          })) || [],
        selected: {
          applicationIds: [],
        },
      });
    } catch (e) {
      reportUnexpectedError(e);
    } finally {
      this.setIsLoading(false);
    }
  }

  setIsLoading(isLoading: boolean) {
    this.setState({ isLoading });
  }

  async downloadSamplePDF() {
    try {
      this.setState({
        isDownloadingSamplePDF: true,
      });
      const { selected } = this.state;
      let applicationId: string;
      if (selected.csv) {
        const csvData = window.atob(selected.csv.data).split("\n");
        if (csvData.length < 2) {
          toast.error("不正なCSVがアップロードされています。");
          return;
        }
        const match = csvData[1].match(/(\d{4}-\d{4}-\d{4}-\d{7})/);
        if (match) {
          applicationId = match[1].replace(/-/g, "");
        } else {
          toast.error("CSVに申請IDがありません。");
          return;
        }
      } else {
        applicationId = selected.applicationIds[0];
      }
      const { data } = await apiClient.outputPDF(applicationId);
      FileSaver.saveAs(new Blob([data]), `${applicationId}.pdf`);
      toast.success("PDFをダウンロードしました。");
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      if (this.handleRetryableError(apiError)) {
        return;
      }
      toast.error(
        "PDFダウンロードに失敗しました。管理者にお問い合わせください。",
      );
      if (
        !SmartApplyApiError.isErrorCode(
          apiError,
          // TODO feedback to user as done in application-detail-service.ts
          "applications.pdf_field_render_failure",
        )
      ) {
        reportUnexpectedError(apiError);
      }
    } finally {
      this.setState({
        isDownloadingSamplePDF: false,
      });
    }
  }

  async batchIssueFiles(
    fileName: string,
    skipNotification: boolean,
  ): Promise<string | undefined> {
    const procId = this.state.selectedProcedureId;
    if (!procId) {
      toast.error("手続きを選択してください。");
      return;
    }
    if (
      this.state.selected.applicationIds.length === 0 &&
      !this.state.selected.csv
    ) {
      toast.error("一括PDF交付の実行対象の申請を選択してください。");
      return;
    }
    const request = this.state.selected.csv
      ? {
          csv: this.state.selected.csv.data,
        }
      : {
          ids: this.state.selected.applicationIds,
        };

    try {
      const { data: bulkOperationResponse } = await apiClient.bulkIssuePdfFile({
        ...request,
        procedureId: procId,
        skipNotification,
        fileName,
      });
      return bulkOperationResponse.id;
    } catch (e) {
      let message: string;
      if (SmartApplyApiError.isRetryable(e)) {
        message = "ネットワークエラーが発生しました。もう一度お試しください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.invalid_csv_format")
      ) {
        message =
          "CSVの形式が不正です。CSVアップロードモーダルにあるサンプルCSVを確認して修正してください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.too_large_record_count")
      ) {
        message = `1度に処理できる申請は500件までです。複数回に分けて操作をしてください。`;
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.procedure_not_match")
      ) {
        message = `「${
          this.state.selectedProcedure?.name ?? ""
        }」以外の手続きに対する申請が選択されています。申請の選択をやり直してください`;
      } else {
        message =
          "一括PDF交付の処理中にエラーが発生しました。時間をおいて再度実行してください。";
        reportUnexpectedError(e);
      }
      toast.error(message);
      return;
    }
  }

  async batchUpdateTenantProcessResult(csv: {
    name: string;
    data: string;
  }): Promise<string | undefined> {
    const procId = this.state.selectedProcedureId;
    if (!procId) {
      toast.error("手続きを選択してください。");
      return;
    }
    try {
      const { data: bulkOperationResponse } =
        await apiClient.bulkUpdateTenantProcessResult({
          procedureId: procId,
          csv: csv.data,
        });
      return bulkOperationResponse.id;
    } catch (e) {
      let msg = "";
      if (
        SmartApplyApiError.isErrorCode(e, "not_found.procedure_before_publish")
      ) {
        msg = "当該の手続きが公開されているか確認してください。";
      } else if (SmartApplyApiError.isErrorCode(e, "forbidden")) {
        msg = "管理情報を一括入力する権限があるか確認してください。";
      } else if (
        SmartApplyApiError.isErrorCode(
          e,
          "applications.tenant_process_result_unchangeable",
        )
      ) {
        msg =
          "当該手続きに対して管理情報の設定がされているか確認してください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.invalid_column_length")
      ) {
        msg = "CSVの列数が一致しているか確認してください。";
      } else if (
        SmartApplyApiError.isErrorCode(
          e,
          "bad_request.invalid_application_id_label",
        )
      ) {
        msg = "「申請ID」の列を含めてアップロードしてください。";
      } else if (
        SmartApplyApiError.isErrorCode(
          e,
          "bad_request.invalid_column_header_label",
        )
      ) {
        msg = "管理情報の全ての項目の列を含めてアップロードしてください。";
      } else if (
        SmartApplyApiError.isErrorCode(
          e,
          "bad_request.invalid_application_id_format",
        )
      ) {
        msg = "申請IDのフォーマットを確認してください。";
      } else if (
        SmartApplyApiError.isErrorCode(e, "bad_request.invalid_character_code")
      ) {
        msg =
          "文字コードが判別できませんでした。CSVをUTF-8に変換して再度お試しください。";
      } else {
        reportUnexpectedError(e);
        msg = "管理情報の一括入力を開始できませんでした。";
      }
      toast.error(msg);
      return;
    }
  }
}
